import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LocationContext = createContext();

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

// Permission states: 'prompt', 'granted', 'denied', 'unavailable'
export const LocationProvider = ({ children }) => {
  const [location, setLocation] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('prompt');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // true once we know the permission status and any auto-fetch has completed
  const [initialized, setInitialized] = useState(false);

  // Check if geolocation is available
  const isGeolocationAvailable = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  // Check permission status on mount
  useEffect(() => {
    if (!isGeolocationAvailable) {
      setPermissionStatus('unavailable');
      setInitialized(true);
      return;
    }

    // Check saved preference
    const savedPreference = localStorage.getItem('exsports-location-preference');
    if (savedPreference === 'denied') {
      setPermissionStatus('denied');
      setInitialized(true);
      return;
    }

    // Check browser permission API if available
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setPermissionStatus(result.state);

        // Listen for permission changes
        result.onchange = () => {
          setPermissionStatus(result.state);
          if (result.state === 'granted') {
            getCurrentLocation();
          } else if (result.state === 'denied') {
            setLocation(null);
            localStorage.setItem('exsports-location-preference', 'denied');
          }
        };

        if (result.state === 'granted') {
          // Auto-fetch location; initialized=true fires after fetch completes
          getCurrentLocation().finally(() => setInitialized(true));
        } else {
          // denied or prompt — no fetch needed, we're done
          setInitialized(true);
        }
      }).catch(() => {
        // Permissions API not supported
        if (savedPreference === 'granted') {
          getCurrentLocation().finally(() => setInitialized(true));
        } else {
          setInitialized(true);
        }
      });
    } else if (savedPreference === 'granted') {
      // Fallback: try to get location if previously granted
      getCurrentLocation().finally(() => setInitialized(true));
    } else {
      setInitialized(true);
    }
  }, [isGeolocationAvailable]);

  // Get current location
  const getCurrentLocation = useCallback(() => {
    if (!isGeolocationAvailable) {
      setError('Geolocation is not available');
      setPermissionStatus('unavailable');
      return Promise.reject(new Error('Geolocation is not available'));
    }

    setIsLoading(true);
    setError(null);

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setLocation(coords);
          setPermissionStatus('granted');
          setIsLoading(false);
          localStorage.setItem('exsports-location-preference', 'granted');
          resolve(coords);
        },
        (err) => {
          setIsLoading(false);
          setError(err.message);

          if (err.code === err.PERMISSION_DENIED) {
            setPermissionStatus('denied');
            localStorage.setItem('exsports-location-preference', 'denied');
          }
          reject(err);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  }, [isGeolocationAvailable]);

  // Request location permission
  const requestLocation = useCallback(async () => {
    try {
      await getCurrentLocation();
      return true;
    } catch (err) {
      return false;
    }
  }, [getCurrentLocation]);

  // Clear location and revoke permission preference
  const clearLocation = useCallback(() => {
    setLocation(null);
    localStorage.setItem('exsports-location-preference', 'denied');
    setPermissionStatus('denied');
  }, []);

  // Reset to prompt state (allows re-requesting)
  const resetPermission = useCallback(() => {
    localStorage.removeItem('exsports-location-preference');
    setPermissionStatus('prompt');
    setError(null);
  }, []);

  const value = {
    // Location data
    location,
    lat: location?.lat,
    lng: location?.lng,

    // Status
    permissionStatus,
    isLocationEnabled: !!location,
    isLoading,
    initialized, // true once permission status is known and any auto-fetch has completed
    error,
    isGeolocationAvailable,

    // Actions
    requestLocation,
    clearLocation,
    resetPermission,
    getCurrentLocation
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};
